import { useEffect, useState } from 'react';

import { isSuperiorEstimate } from '@/presentation/pages/developments/development-detail/utils/is-superior-estimate.js';
import type { Complexity, DevelopmentStatus } from '@/shared/utils/format-status.js';

export type EstimateFormState = {
    complexity: Complexity | '';
    hours: string;
    notes: string;
    delivery: string;
    effortIncreaseReasonId: string;
    effortIncreaseNotes: string;
};

const EMPTY_FORM: EstimateFormState = {
    complexity: '',
    hours: '',
    notes: '',
    delivery: '',
    effortIncreaseReasonId: '',
    effortIncreaseNotes: ''
};

type CreateEstimateParams = {
    complexity: Complexity;
    estimatedHours: number;
    estimatedDeliveryDate?: string;
    notes?: string;
    effortIncreaseReasonId?: string;
    effortIncreaseNotes?: string;
};

export function useEstimateForm(params: {
    devId: string | undefined;
    status: DevelopmentStatus | undefined;
    functionalComplexity: Complexity | null | undefined;
    functionalHours: number | null | undefined;
    liderTecnicoId: string | null | undefined;
    createEstimate: (params: CreateEstimateParams) => Promise<boolean>;
}) {
    const { devId, status, functionalComplexity, functionalHours, liderTecnicoId, createEstimate } = params;
    const [form, setForm] = useState<EstimateFormState>(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);

    const setField = <K extends keyof EstimateFormState>(key: K, value: EstimateFormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        if (status === 'EM_ANALISE_TECNICA' || status === 'ESTIMATIVA_EM_REVISAO') {
            setForm((prev) => ({
                ...prev,
                complexity: functionalComplexity ?? '',
                hours: functionalHours !== null && functionalHours !== undefined ? String(functionalHours) : ''
            }));
        }
    }, [devId, status, functionalComplexity, functionalHours]);

    const isSuperior =
        !!form.complexity &&
        !!form.hours &&
        isSuperiorEstimate({
            estimatedHours: Number(form.hours),
            complexity: form.complexity,
            functionalHours: functionalHours ?? null,
            functionalComplexity: functionalComplexity ?? null
        });

    const submit = async () => {
        if (!form.complexity || !form.hours) {
            return;
        }
        // Espelha SubmitEstimateUseCaseImpl: só segue para desenvolvimento com estimativa funcional e LT definidos.
        if (!liderTecnicoId || functionalComplexity == null || functionalHours == null) {
            return;
        }
        const estimatedHours = Number(form.hours);
        // Reaproveita o `isSuperior` já derivado do mesmo formulário em vez de recalcular: os guards acima
        // garantem exatamente as pré-condições que ele assume (complexity/hours preenchidos, funcional não-nulo),
        // então as duas computações são equivalentes — e uma só evita divergirem no futuro.
        if (isSuperior) {
            if (!form.effortIncreaseReasonId || !form.effortIncreaseNotes.trim()) {
                return;
            }
        } else if (!form.delivery) {
            return;
        }
        setSubmitting(true);
        // Os desenvolvedores não são mais escolhidos aqui: a nomeação passou para a aba Desenvolvimento.
        try {
            const ok = await createEstimate({
                complexity: form.complexity,
                estimatedHours,
                estimatedDeliveryDate: form.delivery || undefined,
                notes: form.notes || undefined,
                effortIncreaseReasonId: isSuperior ? form.effortIncreaseReasonId : undefined,
                effortIncreaseNotes: isSuperior ? form.effortIncreaseNotes.trim() : undefined
            });
            if (!ok) {
                return;
            }
            setForm(EMPTY_FORM);
        } finally {
            setSubmitting(false);
        }
    };

    return { form, setField, isSuperior, submitting, submit };
}
